import { STRAPI_URL, flattenStrapiEntry, parseErrorMessage, strapiAuthHeaders } from './strapi';

export type StrapiFreelanceUserRow = {
  documentId: string;
  numericId: number | null;
  name: string;
  phone_number: string;
  active_status: 'active' | 'inactive' | string;
  user_type?: {
    documentId?: string | null;
    type_code?: string | null;
    type_name?: string | null;
  } | null;
};

export const FREELANCE_MEMBER_KEY_PREFIX = 'freelance:';

export function freelanceMemberKey(documentId: string): string {
  return `${FREELANCE_MEMBER_KEY_PREFIX}${documentId.trim()}`;
}

export function isFreelanceMemberKey(key: string): boolean {
  return key.startsWith(FREELANCE_MEMBER_KEY_PREFIX);
}

export function freelanceDocumentIdFromMemberKey(key: string): string | null {
  if (!isFreelanceMemberKey(key)) return null;
  const id = key.slice(FREELANCE_MEMBER_KEY_PREFIX.length).trim();
  return id || null;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function normalizeRow(raw: Record<string, unknown>): StrapiFreelanceUserRow | null {
  const s = flattenStrapiEntry(raw);
  const documentId = String(raw.documentId ?? s.documentId ?? '').trim();
  if (!documentId) return null;
  const rid = raw.id ?? s.id;
  const utRaw = asRecord(s.user_type) ?? asRecord((raw as { user_type?: unknown }).user_type);
  const utFlat = utRaw ? flattenStrapiEntry(utRaw) : null;
  const statusRaw = String(s.active_status ?? 'active').trim().toLowerCase();
  return {
    documentId,
    numericId: rid != null && Number.isFinite(Number(rid)) ? Number(rid) : null,
    name: String(s.name ?? '').trim(),
    phone_number: String(s.phone_number ?? '').trim(),
    active_status: statusRaw === 'inactive' ? 'inactive' : 'active',
    user_type: utFlat
      ? {
          documentId: utFlat.documentId != null ? String(utFlat.documentId) : null,
          type_code: utFlat.type_code != null ? String(utFlat.type_code) : null,
          type_name: utFlat.type_name != null ? String(utFlat.type_name) : null,
        }
      : null,
  };
}

export type StrapiFreelanceUsersListFilters = {
  status?: 'active' | 'inactive' | 'all';
  userTypeCode?: string | null;
};

export async function strapiListFreelanceUsers(
  jwt: string,
  filters: StrapiFreelanceUsersListFilters = {},
): Promise<StrapiFreelanceUserRow[]> {
  const params = new URLSearchParams({
    'pagination[pageSize]': '200',
    'pagination[page]': '1',
    sort: 'name:asc',
    'populate[user_type][fields][0]': 'documentId',
    'populate[user_type][fields][1]': 'type_code',
    'populate[user_type][fields][2]': 'type_name',
  });
  const status = filters.status ?? 'active';
  if (status !== 'all') {
    params.append('filters[active_status][$eq]', status);
  }
  const typeCode = filters.userTypeCode?.trim();
  if (typeCode) {
    params.append('filters[user_type][type_code][$eq]', typeCode);
  }

  const res = await fetch(`${STRAPI_URL}/api/freelance-users?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const rows = Array.isArray(asRecord(body)?.data)
    ? (asRecord(body)!.data as Record<string, unknown>[])
    : [];
  return rows.map(normalizeRow).filter((row): row is StrapiFreelanceUserRow => row != null);
}

export type StrapiFreelanceUserCreateInput = {
  name: string;
  phone_number?: string;
  active_status?: 'active' | 'inactive';
  /** user-type documentId */
  user_type_document_id: string;
};

export async function strapiCreateFreelanceUser(
  jwt: string,
  input: StrapiFreelanceUserCreateInput,
): Promise<StrapiFreelanceUserRow> {
  const name = input.name.trim();
  if (!name) throw new Error('Nama freelance wajib diisi.');
  const userTypeDocId = input.user_type_document_id.trim();
  if (!userTypeDocId) throw new Error('Tipe user wajib dipilih.');

  const res = await fetch(`${STRAPI_URL}/api/freelance-users?populate[user_type]=true`, {
    method: 'POST',
    headers: {
      ...strapiAuthHeaders(jwt),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: {
        name,
        phone_number: (input.phone_number ?? '').trim() || null,
        active_status: input.active_status ?? 'active',
        user_type: { connect: [userTypeDocId] },
      },
    }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid Strapi freelance user create response');
  const normalized = normalizeRow(row);
  if (!normalized) throw new Error('Invalid Strapi freelance user create response');
  return normalized;
}
